import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

const LOGO = "https://customer-assets-4nw71qhi.emergentagent.net/job_1f616cd5-79d5-4fb8-bbee-901e55ebdd90/artifacts/xmy3jilq_brand-partners-logo.png";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left hero (light) */}
      <div className="relative hidden lg:flex flex-col justify-center px-12 xl:px-20 py-12 bg-gradient-to-br from-slate-50 via-white to-blue-50 overflow-hidden">
        <div className="absolute -top-10 right-0 h-80 w-80 rounded-full bg-blue-300/25 blur-3xl" />
        <div className="absolute bottom-8 left-6 h-64 w-64 rounded-full bg-emerald-300/20 blur-3xl" />

        <div className="relative max-w-xl">
          <img src={LOGO} alt="Brand Partners" className="h-9 mb-12" />

          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 shadow-sm">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">Virtual Sales Management</span>
          </div>

          <h1 className="mt-7 text-[44px] xl:text-6xl font-heading font-extrabold tracking-tight leading-[1.02]">
            <span className="text-slate-900">You already have leads.</span>
            <br />
            <span className="bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent">Let&rsquo;s help you close more of them.</span>
          </h1>

          <p className="mt-6 text-slate-600 text-base xl:text-lg max-w-lg leading-relaxed">
            VSM combines technology, AI, automation and real human follow-up to manage your
            sales opportunities from the moment a lead enters your pipeline through the final sale.
          </p>

          <div className="mt-9 flex items-center gap-2 text-sm text-slate-500">
            <CheckCircle2 className="h-[18px] w-[18px] text-emerald-500 shrink-0" />
            Built for service businesses where every opportunity matters.
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-white">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="lg:hidden mb-8 w-fit">
            <img src={LOGO} alt="Brand Partners" className="h-8" />
          </div>
          <h2 className="text-2xl font-heading font-bold text-slate-900">Welcome back</h2>
          <p className="text-slate-500 mt-1 text-sm">Sign in to your VSM workspace</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required className="mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="mt-1.5 h-11" />
            </div>
            {error && <div data-testid="login-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</div>}
            <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-base">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign In <ArrowRight className="h-4 w-4 ml-1.5" /></>}
            </Button>
          </form>

          <p className="mt-8 text-xs text-slate-400">© 2026 Brand Partners — Award-Winning Digital Agency</p>
        </motion.div>
      </div>
    </div>
  );
}
